import { z } from 'zod'
import { dmMessageDtoSchema, dmParticipantDtoSchema, dmSystemPayloadSchema } from './dm'

export const groupParticipantRoleValues = ['member', 'admin'] as const

/**
 * Who may perform a permission-gated group action: every active member, or only
 * admins. Mirrors WhatsApp's group permission toggles; `all` is the default and
 * matches the historic unrestricted behavior.
 */
export const groupPermissionScopeValues = ['all', 'admins'] as const

export type GroupPermissionScope = (typeof groupPermissionScopeValues)[number]

/** The per-group permission toggles and the invite-link gate, all admin-editable. */
export const groupSettingsDtoSchema = z.object({
  whoCanEditInfo: z.enum(groupPermissionScopeValues),
  whoCanSend: z.enum(groupPermissionScopeValues),
  whoCanAddMembers: z.enum(groupPermissionScopeValues),
  /**
   * When true, redeeming the invite link queues an admin approval request
   * instead of joining. Defaults to false — the historic ungated link behavior.
   */
  requiresJoinApproval: z.boolean(),
})

export type GroupSettingsDto = z.infer<typeof groupSettingsDtoSchema>

/** Partially update the group settings (admin only); omitted keys keep their value. */
export const updateGroupSettingsInputSchema = groupSettingsDtoSchema
  .partial()
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one setting is required',
  })

export type UpdateGroupSettingsInput = z.infer<typeof updateGroupSettingsInputSchema>

/** A group member: their public profile plus their role in the group. */
export const groupParticipantDtoSchema = dmParticipantDtoSchema.extend({
  role: z.enum(groupParticipantRoleValues),
})

export type GroupParticipantDto = z.infer<typeof groupParticipantDtoSchema>

/**
 * Someone who redeemed the invite link while `requiresJoinApproval` was on: they
 * are NOT a participant yet and see nothing of the group until an admin approves.
 */
export const groupJoinRequestDtoSchema = dmParticipantDtoSchema.extend({
  /** When the invite link was redeemed. */
  requestedAt: z.string(),
})

export type GroupJoinRequestDto = z.infer<typeof groupJoinRequestDtoSchema>

/** The group's approval queue, oldest request first. Admins only. */
export const groupJoinRequestsResponseSchema = z.object({
  requests: z.array(groupJoinRequestDtoSchema),
})

export type GroupJoinRequestsResponse = z.infer<typeof groupJoinRequestsResponseSchema>

/** Create a free-standing group: a name and the friends to seed it with (creator excluded). */
export const createGroupInputSchema = z.object({
  name: z.string().min(2).max(80),
  memberIds: z.array(z.string().uuid()).min(1).max(31),
  /** Optional pre-uploaded group photo URL to set on creation. */
  avatarUrl: z.string().url().optional(),
})

export type CreateGroupInput = z.infer<typeof createGroupInputSchema>

/** Summary of a group's most recent message, for the inbox row. */
export const groupLastMessageDtoSchema = z.object({
  kind: z.string(),
  body: z.string().nullable(),
  senderName: z.string().nullable(),
  /** Set only when the last message is a `system` event; localize it client-side. */
  system: dmSystemPayloadSchema.nullable(),
  at: z.string(),
})

export type GroupLastMessageDto = z.infer<typeof groupLastMessageDtoSchema>

/** A group as it appears in the messages tab inbox list. */
export const groupListItemDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string().nullable(),
  /** Group photo URL, or null to fall back to the member avatar stack. */
  avatarUrl: z.string().nullable().optional(),
  /** First 3 members for the avatar stack. */
  participantPreviews: z.array(dmParticipantDtoSchema),
  participantCount: z.number().int(),
  lastMessage: groupLastMessageDtoSchema.nullable(),
  /** Saturates at 100 — a value of 100 means "100 or more", render it as "99+" if you prefer. */
  unreadCount: z.number().int(),
  muted: z.boolean(),
  /** Timestamp the inbox merge sorts on (last message, else group creation). */
  sortAt: z.string(),
  lastMessageAt: z.string().nullable(),
})

export type GroupListItemDto = z.infer<typeof groupListItemDtoSchema>

/** Full group detail for the chat header and participants sheet. */
export const groupDetailDtoSchema = z.object({
  /** The profile running this group from its dashboard, who reads along; null for a private group. */
  organizer: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      accountUserId: z.string().uuid().nullable(),
    })
    .nullable()
    .optional(),
  id: z.string().uuid(),
  title: z.string().nullable(),
  /** Free-text group description, editable under `whoCanEditInfo`. */
  description: z.string().nullable(),
  /** Group photo URL, or null to fall back to the member avatar stack. */
  avatarUrl: z.string().nullable().optional(),
  participants: z.array(groupParticipantDtoSchema),
  myRole: z.enum(groupParticipantRoleValues),
  /** The group creator: never demotable or removable by other admins. */
  ownerId: z.string().uuid().nullable(),
  muted: z.boolean(),
  /** When the current user's mute expires; far-future for an indefinite mute, null when unmuted. */
  mutedUntil: z.string().nullable(),
  disappearingTtlSeconds: z.number().int().nullable(),
  /** The group settings; only admins may change them. */
  settings: groupSettingsDtoSchema,
  /** Invite-link requests waiting for a decision — exposed to admins only, else 0. */
  pendingJoinRequestCount: z.number().int(),
  /** The active invite code — exposed to admins only, `null` for members and when unset. */
  inviteCode: z.string().nullable(),
  /**
   * Oldest `lastReadAt` across the OTHER active members, or null while anyone
   * has not read yet — drives the double-tick on the current user's messages.
   */
  othersReadAt: z.string().nullable(),
  /**
   * The viewer's own read pointer, as it stood before this request. The unread
   * divider anchors at the oldest incoming message newer than it; null when the
   * viewer has never read this group.
   */
  myReadAt: z.string().nullable(),
})

export type GroupDetailDto = z.infer<typeof groupDetailDtoSchema>

/** Add friends to an existing group; gated by the group's `whoCanAddMembers` setting. */
export const addGroupMembersInputSchema = z.object({
  memberIds: z.array(z.string().uuid()).min(1).max(31),
})

export type AddGroupMembersInput = z.infer<typeof addGroupMembersInputSchema>

/** Rename a group; gated by the group's `whoCanEditInfo` setting. */
export const renameGroupInputSchema = z.object({
  name: z.string().min(2).max(80),
})

export type RenameGroupInput = z.infer<typeof renameGroupInputSchema>

/** Set or clear (with null) the group description; gated by `whoCanEditInfo`. */
export const setGroupDescriptionInputSchema = z.object({
  description: z.string().max(500).nullable(),
})

export type SetGroupDescriptionInput = z.infer<typeof setGroupDescriptionInputSchema>

/** Promote a member to admin or demote an admin back to member (admin only). */
export const setGroupMemberRoleInputSchema = z.object({
  role: z.enum(groupParticipantRoleValues),
})

export type SetGroupMemberRoleInput = z.infer<typeof setGroupMemberRoleInputSchema>

export const groupMuteDurationValues = ['8h', '1w', 'always'] as const

export type GroupMuteDuration = (typeof groupMuteDurationValues)[number]

/** Mute a group for a bounded window (or indefinitely), or unmute it. */
export const setGroupMutedInputSchema = z.object({
  muted: z.boolean(),
  /** Applies when muting; omitted means an indefinite mute. */
  duration: z.enum(groupMuteDurationValues).optional(),
})

export type SetGroupMutedInput = z.infer<typeof setGroupMutedInputSchema>

export const setGroupMutedResponseSchema = z.object({
  muted: z.boolean(),
  mutedUntil: z.string().nullable(),
})

export type SetGroupMutedResponse = z.infer<typeof setGroupMutedResponseSchema>

/** The group's shareable invite link — anyone holding the code may join without a friendship. */
export const groupInviteLinkResponseSchema = z.object({
  code: z.string(),
  url: z.string(),
})

export type GroupInviteLinkResponse = z.infer<typeof groupInviteLinkResponseSchema>

/**
 * How redeeming an invite code ended:
 * - `joined` — the caller is now an active member.
 * - `already_member` — the caller was already active; nothing changed.
 * - `pending` — the group gates link joins, so a request is queued for the
 *   admins and the caller is still an outsider.
 */
export const joinGroupByCodeStatusValues = ['joined', 'already_member', 'pending'] as const

export type JoinGroupByCodeStatus = (typeof joinGroupByCodeStatusValues)[number]

export const joinGroupByCodeResponseSchema = z.object({
  /** The group behind the code — only openable when `status` is not `pending`. */
  id: z.string().uuid(),
  status: z.enum(joinGroupByCodeStatusValues),
})

export type JoinGroupByCodeResponse = z.infer<typeof joinGroupByCodeResponseSchema>

/** The group's photo URL after a successful upload. */
export const setGroupAvatarResponseSchema = z.object({
  avatarUrl: z.string(),
})

export type SetGroupAvatarResponse = z.infer<typeof setGroupAvatarResponseSchema>

export const createGroupResponseSchema = z.object({
  id: z.string().uuid(),
})

export type CreateGroupResponse = z.infer<typeof createGroupResponseSchema>

/**
 * A group chat message. Groups reuse the 1:1 DM message contract verbatim — the
 * same `kind` + `metadata` rich-card payloads (event / listing / place / location /
 * plan_vote), so the group screen renders the identical cards. Unlike a 1:1 chat it
 * also surfaces the per-message sender identity already carried by
 * `senderId` / `senderName` / `senderAvatarUrl`.
 */
export const groupMessageDtoSchema = dmMessageDtoSchema

export type GroupMessageDto = z.infer<typeof groupMessageDtoSchema>

/**
 * One other active member's read state for a single group message. Pulsify tracks
 * reading as a per-participant pointer (`lastReadAt`), not a per-message ledger, so
 * `read` means the member's pointer is at or past the message and `readAt` is that
 * pointer — the moment they last opened the group, not the moment they saw this
 * message. There is deliberately no delivered state: none is recorded.
 */
export const groupMessageReadByEntryDtoSchema = dmParticipantDtoSchema.extend({
  read: z.boolean(),
  /** The member's read pointer when `read` is true, else null. */
  readAt: z.string().nullable(),
})

export type GroupMessageReadByEntryDto = z.infer<typeof groupMessageReadByEntryDtoSchema>

/**
 * Per-member read state for one of the viewer's own group messages (WhatsApp
 * "Message info"). Only the message's sender may read it.
 */
export const groupMessageReadByResponseSchema = z.object({
  messageId: z.string().uuid(),
  /** Every other active member, those who have read it first (most recent pointer first). */
  entries: z.array(groupMessageReadByEntryDtoSchema),
})

export type GroupMessageReadByResponse = z.infer<typeof groupMessageReadByResponseSchema>

export const groupMessagesResponseSchema = z.object({
  messages: z.array(groupMessageDtoSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type GroupMessagesResponse = z.infer<typeof groupMessagesResponseSchema>

export const sendGroupMessageResponseSchema = z.object({
  message: groupMessageDtoSchema,
})

export type SendGroupMessageResponse = z.infer<typeof sendGroupMessageResponseSchema>
